// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { IEventTelemetry } from '@microsoft/applicationinsights-web';
import { cloneDeep, isFunction } from 'lodash';

import { REQUEST_RESPONSE_EVENT_NAME } from '../constants';
import CorrelationVector from './../../../lib/CorrelationVector';

// Max string value is 8kb: https://github.com/MicrosoftDocs/azure-docs/blob/master/includes/application-insights-limits.md
// Telemetry item's max size is 64kb, but the item seems to get stringified before sent out so the whole item's serialized size will also be capped at 8kb
const KILOBYTE = 1024;
const DEEPLY_NESTED_THRESHOLD = 2;
const MAX_DEEPLY_NESTED_KEY_VALUE_SIZE = 0.25*KILOBYTE;
const MAX_REQUEST_PAYLOAD_SIZE = 1.5*KILOBYTE;
const MAX_RESPONSE_PAYLOAD_SIZE = 4.5*KILOBYTE;

const EXCLUDED_REQUEST_HEADERS = ['AUTHORIZATION'];
const SERIALIZATION_EXCLUSIONS = ['TRANSFORMREQUEST', 'TRANSFORMRESPONSE'];

export function createRequestResponse(request?: any, responseOrError?: any, isError: boolean = false): IEventTelemetry | undefined {
    try {
        let requestToLog = cloneDeep(request || {});
        let responseToLog = cloneDeep(responseOrError || {});
        const correlationVector = requestToLog.headers && requestToLog.headers['ms-cv'] || CorrelationVector.getCurrentValue();
        
        // Strip out auth header from request
        if (requestToLog.headers) {
            Object.keys(requestToLog.headers).forEach((key: string) => {
                if (EXCLUDED_REQUEST_HEADERS.includes(key.toUpperCase())) {
                    delete requestToLog.headers[key];
                }
            });
        }
        if (responseToLog.config && responseToLog.config.headers) {
            Object.keys(responseToLog.config.headers).forEach((key: string) => {
                if (EXCLUDED_REQUEST_HEADERS.includes(key.toUpperCase())) {
                    delete responseToLog.config.headers[key];
                }
            });
        }
        
        // Ensure the telemetry isn't too large and exceeds the buffer size -- assume max size of a telemetry item is 8kb
        requestToLog = minifyRequestResponse(requestToLog);
        responseToLog = minifyRequestResponse(responseToLog, isError);
        return {
            name: REQUEST_RESPONSE_EVENT_NAME,
            properties: {
                correlationVector,
                isError,
                request: requestToLog,
                requestName: request && `${request.method.toUpperCase()} ${request.url}`,
                response: responseToLog,
            },
        } as IEventTelemetry;
    } catch (error) {
        // Fallback if any of this complicated serialization ever fails
        return {
            name: REQUEST_RESPONSE_EVENT_NAME,
            properties: {
                isError,
                errorConstructingTelemetry: error.message,
                request: JSON.stringify(request || {}, replacer, 0).substring(0, MAX_REQUEST_PAYLOAD_SIZE),
                requestName: `${request && request.method.toUpperCase()} ${request && request.url}`,
                response: JSON.stringify(responseOrError || {}, replacer, 0).substring(0, MAX_RESPONSE_PAYLOAD_SIZE),
                responseStatus: responseOrError && (responseOrError.status || (responseOrError.response && responseOrError.response.status)),
            },
        } as IEventTelemetry;
    }
}

function minifyObject(value: any, depth: number = 0, modifyKeys: boolean = false) {
    if (typeof value !== 'object' || value === undefined || value === null) {
        return value;
    }
    const copy = {};
    Object.getOwnPropertyNames(value).forEach((key: string) => {
        // Depending on depth of the object, truncate the value and change the key (especially for Error objects that do not serialize properly based on key names)
        // Top-level keys need to be modified, but lower level like error.response.status may not need to be
        const keyToWrite = modifyKeys && !key.toUpperCase().includes('STATUS') && depth < 2 ? `_${key}` : key;
        const keyValue = SERIALIZATION_EXCLUSIONS.includes(key.toUpperCase()) ? undefined : minifyObject(value[key], depth+1);
        
        if (keyValue && depth > DEEPLY_NESTED_THRESHOLD && typeof keyValue === 'object') {
            copy[keyToWrite] = JSON.stringify(keyValue, replacer, 0).substring(0, MAX_DEEPLY_NESTED_KEY_VALUE_SIZE);
        } else {
            copy[keyToWrite] = keyValue;
        }
    });
    return copy;
}

function minifyRequestResponse(requestResponse: any, isError: boolean = false): any {
    if (isError) {
        const errorCopy = {
            ...requestResponse,
            // Copy out the nested 'response' key's values to make AI queries simpler
            ...(requestResponse && requestResponse.response),
            // Remove the old 'response' key-value
            ...(requestResponse && requestResponse.response && { response:  undefined }),
        };
        // Error objects don't serialize properly on their own
        return JSON.stringify(minifyObject(errorCopy, 0, true), replacer, 0).substring(0, MAX_RESPONSE_PAYLOAD_SIZE);
    }
    if (!requestResponse) {
        return '';
    }

    if (requestResponse.data) {
        requestResponse.data = JSON.stringify(minifyObject(requestResponse.data, 1), replacer, 0).substring(0, MAX_RESPONSE_PAYLOAD_SIZE);
    }
    if (requestResponse.config) {
        if (requestResponse.config.data) {
            // This should just be a dupe of the request body so keep even less of it
            requestResponse.config.data = JSON.stringify(minifyObject(requestResponse.config.data, 2), replacer, 0).substring(0, MAX_REQUEST_PAYLOAD_SIZE);
        }
    }
    
    return minifyObject(requestResponse, 0);
}

function replacer(key: string, value: any) {
    // https://github.com/winstonjs/logform/pull/84/commits/28109ea26c87e304b73d00920ca89bfa47cce42a
    //comment line 127 - 129
    // if (value instanceof Buffer) {
    //     return value.toString('base64');
    // }
    if (typeof value === 'bigint') {
        return value.toString();
    }

    // Omit functions and known keys that are useless for debugging
    if (isFunction(value)) {
        return undefined;
    }
    if (SERIALIZATION_EXCLUSIONS.includes(key.toUpperCase())) {
        return undefined;
    }
    return value;
}
